<template>
  <Card no-shadow class="equipment-card">
    <template #title>
      <div class="equipment-card__title">
        <h4 class="equipment-card__heading">
          <ArchivedLabel :disabled="!archived">
            {{ name }}
          </ArchivedLabel>
        </h4>
        <button
          v-if="onEdit"
          type="button"
          class="equipment-card__edit"
          aria-label="Edit equipment"
          @click="handleEdit"
        >
          <i class="fas fa-edit"></i>
        </button>
      </div>
    </template>
    <template #body>
      <dl class="equipment-card__fields">
        <div v-if="equipmentTypeName" class="equipment-card__field">
          <dt>Type</dt>
          <dd>{{ equipmentTypeName || emptyDisplay }}</dd>
        </div>
        <div class="equipment-card__field">
          <dt>Oldest component</dt>
          <dd>{{ oldestComponentInstalledDisplay }}</dd>
        </div>
        <div class="equipment-card__field">
          <dt>Last serviced</dt>
          <dd>{{ lastServicedDate }}</dd>
        </div>
        <div v-if="address" class="equipment-card__field">
          <dt>Address</dt>
          <dd>{{ address }}</dd>
        </div>
        <div class="equipment-card__field">
          <dt>Notes</dt>
          <dd class="equipment-card__notes">{{ notes || emptyDisplay }}</dd>
        </div>
      </dl>
      <div v-if="warranty" class="equipment-card__warranty">
        <div class="equipment-card__warranty-block">
          <span class="equipment-card__eyebrow">{{
            warranty.warranty_program_name
          }}</span>
          <div class="equipment-card__warranty-row">
            Provider: {{ warranty.warranty_program_provider_name }}
          </div>
          <div
            v-if="showWarrantyLaborRate"
            class="equipment-card__warranty-row"
          >
            Labor rate: {{ currency(warranty.labor_rate_cents / 100) }}
          </div>
          <div class="equipment-card__warranty-row">
            Term: {{ warranty.term_years }} yr
          </div>
        </div>
        <div
          v-if="showWarrantyPurchaseDate"
          class="equipment-card__warranty-block"
        >
          <span class="equipment-card__eyebrow">Purchase date</span>
          <div class="equipment-card__warranty-row">
            {{ formatDate(warranty.purchase_date) }}
          </div>
        </div>
        <div class="equipment-card__warranty-block">
          <span class="equipment-card__eyebrow">Effective date</span>
          <div class="equipment-card__warranty-row">
            {{ formatDate(warranty.effective_date) }}
          </div>
        </div>
        <div class="equipment-card__warranty-block">
          <span class="equipment-card__eyebrow">Expiration date</span>
          <div class="equipment-card__warranty-row">
            {{ formatDate(warranty.expiration_date) }}
          </div>
        </div>
      </div>
      <div class="equipment-card__sections">
        <section class="equipment-card__section">
          <button
            type="button"
            class="equipment-card__section-header"
            :aria-expanded="!actualCollapsedComponents"
            @click="handleToggleCollapseComponents"
          >
            <span class="equipment-card__section-title">
              Components
              <span class="equipment-card__count"
                >({{ components.length }})</span
              >
            </span>
            <i
              class="fas equipment-card__caret"
              :class="
                actualCollapsedComponents ? 'fa-angle-down' : 'fa-angle-up'
              "
            ></i>
          </button>
          <div
            v-if="!actualCollapsedComponents"
            class="equipment-card__section-body"
          >
            <slot name="componentsTable">
              <ComponentTable :components="components" />
            </slot>
          </div>
        </section>
        <section v-if="showServiceHistory" class="equipment-card__section">
          <button
            type="button"
            class="equipment-card__section-header"
            :aria-expanded="!actualCollapsedServiceHistory"
            @click="handleToggleCollapseServiceHistory"
          >
            <span class="equipment-card__section-title">
              Service History
              <span class="equipment-card__count"
                >({{ serviceHistory.length }})</span
              >
            </span>
            <i
              class="fas equipment-card__caret"
              :class="
                actualCollapsedServiceHistory ? 'fa-angle-down' : 'fa-angle-up'
              "
            ></i>
          </button>
          <div
            v-if="!actualCollapsedServiceHistory"
            class="equipment-card__section-body"
          >
            <slot name="serviceHistoryTable">
              <ServiceHistoryTable :service-history="serviceHistory" />
            </slot>
          </div>
        </section>
      </div>
    </template>
  </Card>
</template>

<script>
import sortBy from "lodash/sortBy";
import Card from "serviceshift-ui/components/Card/Card.vue";
import ComponentTable from "serviceshift-ui/components/Equipment/ComponentTable.vue";
import ServiceHistoryTable from "serviceshift-ui/components/Equipment/ServiceHistoryTable.vue";
import ArchivedLabel from "serviceshift-ui/components/General/ArchivedLabel.vue";
import ComponentType from "serviceshift-ui/shared/src/classes/componentType.js";
import { ServiceHistoryRecord } from "serviceshift-ui/shared/src/classes/serviceHistory";
import currency from "serviceshift-ui/shared/src/filters/currency.js";
import {
  emptyDisplay,
  formatDate,
  yearsAndMonthsFromNow
} from "serviceshift-ui/shared/src/mixins";

export default {
  name: "EquipmentCard",
  components: {
    Card,
    ArchivedLabel,
    ComponentTable,
    ServiceHistoryTable
  },
  props: {
    equipmentTypeName: {
      type: String,
      default: () => ""
    },
    /** @type { ServiceHistoryRecord[] } */
    serviceHistory: {
      type: Array,
      default: () => []
    },
    warranty: {
      type: Object,
      default: () => undefined
    },
    showWarrantyPurchaseDate: {
      type: Boolean,
      default: () => true
    },
    showWarrantyLaborRate: {
      type: Boolean,
      default: () => true
    },
    showServiceHistory: {
      type: Boolean,
      default: () => true
    },
    name: {
      type: String,
      default: () => ""
    },
    address: {
      type: String,
      default: () => ""
    },
    notes: {
      type: String,
      default: () => ""
    },
    archived: {
      type: Boolean,
      default: () => false
    },
    /** @type { ComponentType[] } */
    components: {
      type: Array,
      default: () => []
    },
    collapsedComponents: {
      type: Boolean,
      default: () => undefined
    },
    collapsedServiceHistory: {
      type: Boolean,
      default: () => undefined
    },
    defaultCollapsed: {
      type: Boolean,
      default: () => undefined
    },
    onEdit: {
      type: Function,
      default: null
    }
  },
  emits: [
    "edit",
    "update:collapsed-components",
    "update:collapsed-service-history"
  ],
  data: function () {
    return {
      emptyDisplay,
      internalComponentsCollapsed:
        this.defaultCollapsed !== undefined ? this.defaultCollapsed : false,
      internalServiceHistoryCollapsed:
        this.defaultCollapsed !== undefined ? this.defaultCollapsed : false
    };
  },
  computed: {
    oldestComponentInstalledDisplay() {
      /** @type { ComponentType[] } */
      const components = this.components.slice(0);
      if (!this.components.length) return emptyDisplay;
      const installedComponents = components
        .map((component) => ({
          ...component,
          calculatedInstallDate:
            component.installed_on || component.manufactured_on
        }))
        .filter((component) => !!component.calculatedInstallDate);
      if (!installedComponents.length) return emptyDisplay;
      const oldestComponent = sortBy(installedComponents, (component) =>
        new Date(component.calculatedInstallDate).getTime()
      )[0];
      return yearsAndMonthsFromNow(oldestComponent.calculatedInstallDate);
    },
    lastServicedDate() {
      /** @type { ServiceHistoryRecord[] } */
      const serviceHistoryRecords = this.serviceHistory.slice(0);
      if (!serviceHistoryRecords.length) return emptyDisplay;
      const latestServiceRecord = sortBy(serviceHistoryRecords, (record) =>
        new Date(record.invoice_finalized_at).getTime()
      ).reverse()[0];
      return formatDate(new Date(latestServiceRecord.invoice_finalized_at));
    },
    actualCollapsedComponents: {
      get() {
        if (this.collapsedComponents !== undefined)
          return this.collapsedComponents;
        return this.internalComponentsCollapsed;
      },
      set(val) {
        this.$emit("update:collapsed-components", val);
        this.internalComponentsCollapsed = val;
      }
    },
    actualCollapsedServiceHistory: {
      get() {
        if (this.collapsedServiceHistory !== undefined)
          return this.collapsedServiceHistory;
        return this.internalServiceHistoryCollapsed;
      },
      set(val) {
        this.$emit("update:collapsed-service-history", val);
        this.internalServiceHistoryCollapsed = val;
      }
    }
  },
  methods: {
    currency,
    formatDate,
    handleEdit() {
      this.$emit("edit");
    },
    handleToggleCollapseComponents() {
      this.actualCollapsedComponents = !this.actualCollapsedComponents;
    },
    handleToggleCollapseServiceHistory() {
      this.actualCollapsedServiceHistory = !this.actualCollapsedServiceHistory;
    }
  }
};
</script>

<style lang="scss" scoped>
.equipment-card {
  // serviceshift-ui Card has no noShadow prop — the box-shadow lives on
  // BaseCard's root .card div. Override on the same node directly.
  box-shadow: none !important;
  border-color: #e2e8f0;

  :deep(.card-header) {
    margin-bottom: 0 !important;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f1f5f9;
  }
  :deep(.card-body) {
    padding-top: 1rem;
  }
}

.equipment-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;
}
.equipment-card__heading {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: #0f172a;
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.equipment-card__edit {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0.25rem;
  margin-left: 0.125rem;
  color: var(--blue);
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
  font-size: 0.8125rem;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: #eff6ff;
  }
  &:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 1px;
  }
}

.equipment-card__fields {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.equipment-card__field {
  display: grid;
  grid-template-columns: 9.5rem minmax(0, 1fr);
  align-items: baseline;
  gap: 0.75rem;

  dt {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
    margin: 0;
    padding-top: 0.0625rem;
  }
  dd {
    margin: 0;
    font-size: 0.875rem;
    color: #0f172a;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.125rem;
  }
}
.equipment-card__notes {
  white-space: pre-line;
}

.equipment-card__warranty {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-top: 1.25rem;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.75rem;
  }
}
.equipment-card__warranty-block {
  min-width: 0;
}
.equipment-card__warranty-row {
  font-size: 0.875rem;
  color: #0f172a;
  margin-top: 0.125rem;
}

.equipment-card__eyebrow {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.equipment-card__sections {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
}
.equipment-card__section {
  & + & {
    margin-top: 0.25rem;
    border-top: 1px solid #f1f5f9;
    padding-top: 0.25rem;
  }
}
.equipment-card__section-header {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0.5rem 0.25rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: #0f172a;
  border-radius: 4px;
  transition: background 120ms ease;

  &:hover {
    background: #f8fafc;
  }
  &:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 1px;
  }
}
.equipment-card__section-title {
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: #0f172a;
}
.equipment-card__count {
  color: #64748b;
  font-weight: 500;
  margin-left: 0.125rem;
}
.equipment-card__caret {
  color: #64748b;
  font-size: 1rem;
  transition: color 120ms ease;
}
.equipment-card__section-body {
  padding: 0.5rem 0 0;
}
</style>
